import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { FieldErrorDirective, FormFieldComponent } from '@shared/components/form-field';
import { InputComponent } from '@shared/components/input';
import { SelectComponent } from '@shared/components/select';
import { ToggleComponent } from '@shared/components/toggle';

import type { SelectOption } from '@shared/components/dropdown/dropdown.types';
import type { Signal } from '@angular/core';
import { AfterViewInit, Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';
import { DateRangePickerComponent } from '../../../../partage/components/date-range-picker/date-range-picker.component';
import { AcompteAnnulationService } from '../dialog-acompte-annulation/acompte-annulation.service';
import { RedditionAnnulationService } from '../dialog-reddition-annulation/reddition-annulation-service';
import { EvenementFilterForm } from '../evenements.interface';
import { EvenementsService } from '../evenements.service';

@Component({
  selector: 'app-evenement-filtre',
  imports: [
    ReactiveFormsModule,
    FormFieldComponent,
    FieldErrorDirective,
    InputComponent,
    SelectComponent,
    ToggleComponent,
    DateRangePickerComponent,
  ],
  templateUrl: './evenement-filtre.component.html',
  styleUrl: './evenement-filtre.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: { dateFormat: 'dd/MM/yyyy HH:mm:ss', timezone: '+0000' },
    },
  ],
})
export class EvenementFiltreComponent implements AfterViewInit {
  private evenementService = inject(EvenementsService);
  private acompteAnnulationService = inject(AcompteAnnulationService);
  private redditionAnnulationService = inject(RedditionAnnulationService);
  private route = inject(ActivatedRoute);
  evenements = this.evenementService.evenement;
  clients = this.evenementService.clients;
  clientSelection = this.evenementService.clientSelection;
  aRedditionnerIsChecked = signal(false);
  estArchiveIsChecked = signal(false);

  filterForm: FormGroup<EvenementFilterForm>;

  // Recalculé à chaque validation du champ, y compris sur un reset du formulaire
  protected readonly rechercheTropCourte: Signal<boolean>;

  ngAfterViewInit() {
    this.route.queryParamMap.subscribe((params: ParamMap) => {
      const paramClient = params.get('codeClient');
      const paramARedditionner = params.get('aRedditionner');

      this.evenementService.urlClient.set(paramClient ?? '');

      if (paramARedditionner === 'true') {
        this.toggleARedditionnerChange();
      }
    });
  }

  // `app-select` raisonne en clés : le code client sert de clé
  protected optionsClients = computed<SelectOption[]>(() =>
    this.clients().map(client => ({ value: client.codeClient, label: client.libelleClient })),
  );

  protected clientsSelectionnes = computed<string[]>(() => {
    const client = this.clientSelection();
    return client ? [client.codeClient] : [];
  });

  protected choisirClient(codes: readonly string[]): void {
    this.evenementService.selectionnerClientParCode(codes[0]);
  }

  clearClient() {
    this.evenementService.selectionnerClientParCode(undefined);
  }

  clearEvenement() {
    this.filterForm.controls.rechercheEvenement.setValue('');
  }
  clearDate() {
    this.filterForm.controls.filtreDateDebut.reset();
    this.filterForm.controls.filtreDateFin.reset();
  }

  toggleARedditionnerChange() {
    this.aRedditionnerIsChecked.update(coche => !coche);
    this.clearClient();
    this.filterForm.reset();
    this.filterForm.controls.aRedditionnerUniquement.setValue(this.aRedditionnerIsChecked());
    this.estArchiveIsChecked.set(false);
    this.filterForm.controls.estArchive.setValue(false);
  }

  toggleEstArchiveChange() {
    this.estArchiveIsChecked.update(coche => !coche);
    this.filterForm.controls.estArchive.setValue(this.estArchiveIsChecked());
    this.aRedditionnerIsChecked.set(false);
    this.filterForm.controls.aRedditionnerUniquement.setValue(false);
  }

  constructor() {
    this.filterForm = new FormGroup<EvenementFilterForm>({
      codeClients: new FormControl<string[]>(
        {
          value: [],
          disabled: false,
        },
        { nonNullable: true },
      ),
      rechercheEvenement: new FormControl<string>(
        { value: '', disabled: false },
        { nonNullable: true, validators: [Validators.minLength(3)] },
      ),
      aRedditionnerUniquement: new FormControl<boolean>({ value: false, disabled: false }, { nonNullable: true }),
      estArchive: new FormControl<boolean>({ value: false, disabled: false }, { nonNullable: true }),
      filtreDateDebut: new FormControl<string>({ value: '', disabled: false }, { nonNullable: true }),
      filtreDateFin: new FormControl<string>({ value: '', disabled: false }, { nonNullable: true }),
    });

    const recherche = this.filterForm.controls.rechercheEvenement;
    this.rechercheTropCourte = toSignal(recherche.statusChanges.pipe(map(() => recherche.hasError('minlength'))), {
      initialValue: recherche.hasError('minlength'),
    });

    this.filterForm.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
      if (this.filterForm.valid) {
        this.evenementService.filterform.set(this.filterForm.getRawValue());
      }
    });

    effect(() => {
      this.filterForm.controls.codeClients.patchValue(
        this.clientSelection() ? [this.clientSelection()!.codeClient] : [],
        { emitEvent: true },
      );
    });

    effect(() => {
      if (this.acompteAnnulationService.acompteAnnule()) {
        this.filterForm.reset(this.filterForm.value);
        this.acompteAnnulationService.acompteAnnule.set(false);
      }
    });

    effect(() => {
      if (this.redditionAnnulationService.redditionAnnulee()) {
        this.filterForm.reset(this.filterForm.value);
        this.redditionAnnulationService.redditionAnnulee.set(false);
      }
    });
  }
}
